import L from 'leaflet'
import moment from 'moment'

const ECMC_BASE = 'https://ecmc.state.co.us/cogisdb'

const ONE_MILE_M = 1609.34
const TWO_THOUSAND_FT_M = 609.6

export const BASEMAPS: Record<string, L.TileLayer> = {
  'CartoDB Positron': L.tileLayer(`https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png?key=${import.meta.env.VITE_CARTO_API_KEY}`, {
    maxZoom: 19,
    subdomains: 'abcd',
    attribution: '&copy; <a href="https://ecocarto.com/" target="_blank">EcoCarto</a>, &copy; <a href="https://carto.com/attributions" target="_blank">CARTO</a>',
  }),
  'OpenStreetMap': L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
    maxZoom: 19, opacity: 0.7,
    attribution: '&copy; <a href="https://ecocarto.com/" target="_blank">EcoCarto</a>, &copy; <a href="https://www.openstreetmap.org/copyright" target="_blank">OpenStreetMap</a> contributors',
  }),
  'Stadia Maps': L.tileLayer('https://tiles.stadiamaps.com/tiles/alidade_smooth/{z}/{x}/{y}{r}.png', {
    maxZoom: 20,
    attribution: '&copy; <a href="https://ecocarto.com/" target="_blank">EcoCarto</a>, &copy; <a href="https://stadiamaps.com/" target="_blank">Stadia Maps</a>, &copy; <a href="https://openmaptiles.org/" target="_blank">OpenMapTiles</a>, &copy; <a href="https://www.openstreetmap.org/copyright" target="_blank">OpenStreetMap</a>',
  }),
  'Esri World Imagery': L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', {
    maxZoom: 19, opacity: 0.7,
    attribution: '&copy; <a href="https://ecocarto.com/" target="_blank">EcoCarto</a>, Tiles &copy; Esri — Source: Esri, Maxar, Earthstar Geographics, and the GIS User Community',
  }),
  'Google Satellite': L.tileLayer('https://www.google.cn/maps/vt?lyrs=s@189&gl=cn&x={x}&y={y}&z={z}', {
    maxZoom: 20, opacity: 0.7,
    attribution: '&copy; <a href="https://ecocarto.com/" target="_blank">EcoCarto</a>, &copy; <a href="https://www.google.com/maps" target="_blank">Google Maps</a>',
  }),
}

export const Colors = {
  fallback:    '#FF0000',
  well_lp:     '#ff9900',
  well_pa:     '#999999',
  well_active: '#cc0000',
}

export interface OgdpDefinition {
  id: string
  file: string
  schoolCodes: string[]
}

// Add an entry here for each new OGDP GeoJSON. Its features are expected to be
// named `<id>_pad`, `<id>_2000ft`, and `<id>_1mile` (see ogdpStyleFor below).
export const OGDPS: OgdpDefinition[] = [
  { id: 'century', file: 'century_pad.geojson', schoolCodes: ['0498', '7330'] },
]

type OgdpKind = 'pad' | '2000ft' | '1mile'

const OGDP_STYLES: Record<OgdpKind, L.PathOptions> = {
  pad:      { color: '#8d5a99', fillColor: '#8d5a99', fillOpacity: 0.6, weight: 2, dashArray: '6,6', interactive: false },
  '2000ft': { color: '#cc0000', fillColor: '#cc0000', fillOpacity: 0.05, weight: 2, dashArray: '6,6', interactive: false },
  '1mile':  { color: '#ff7f00', fillColor: '#ff7f00', fillOpacity: 0.03, weight: 2, dashArray: '6,6', interactive: false },
}

// Target how many times each perimeter label repeats around its ring.
// The actual padding needed to hit that count is measured against the
// live rendered path (see computeLabelRepeat) since it depends on the
// ring's on-screen pixel length, which varies by shape and zoom level.
const OGDP_LABEL_TARGET_COUNT: Record<'2000ft' | '1mile', number> = {
  '2000ft': 2,
  '1mile': 3,
}

// leaflet-textpath tiles a (possibly nbsp-padded) copy of the label to fill
// the entire path, which is what makes it wrap evenly around the ring. We
// measure the rendered path length and the label's rendered width (both in
// screen px) so the padding yields close to targetCount copies, instead of
// guessing a fixed pad length that only holds at one specific zoom/shape.
function computeLabelRepeat(path: SVGPathElement, renderer: SVGElement, label: string, targetCount: number, attributes: Record<string, string>): number {
  const measure = (text: string): number => {
    const node = L.SVG.create('text')
    for (const key in attributes) node.setAttribute(key, attributes[key])
    node.appendChild(document.createTextNode(text))
    renderer.appendChild(node)
    const width = node.getComputedTextLength()
    renderer.removeChild(node)
    return width
  }

  const targetInstanceWidth = path.getTotalLength() / targetCount
  const labelWidth = measure(label)
  const nbspWidth = measure(' ') || 4
  const extraChars = Math.max(0, Math.ceil((targetInstanceWidth - labelWidth) / nbspWidth))
  return label.length + extraChars
}

function attachPerimeterLabel(polyline: L.Polyline, label: string, color: string, targetCount: number): void {
  const attributes = { fill: color, 'font-size': '13', 'font-weight': 'bold', stroke: '#fff', 'stroke-width': '1', 'paint-order': 'stroke' }
  polyline.on('add', () => {
    const path = (polyline as unknown as { _path?: SVGPathElement })._path
    const renderer = (polyline as unknown as { _renderer?: { _container: SVGElement } })._renderer?._container
    if (!path || !renderer) return
    const repeat = computeLabelRepeat(path, renderer, label, targetCount, attributes)
    polyline.setText(label, { center: true, offset: -6, repeat, attributes })
  })
}

function ogdpKindFor(name: string): OgdpKind | undefined {
  if (name.endsWith('_pad')) return 'pad'
  if (name.endsWith('_2000ft')) return '2000ft'
  if (name.endsWith('_1mile')) return '1mile'
  return undefined
}

function ogdpStyleFor(name: string): L.PathOptions {
  const kind = ogdpKindFor(name)
  return (kind && OGDP_STYLES[kind]) ?? { color: Colors.fallback, fillOpacity: 0, weight: 2, dashArray: '6,6', interactive: false }
}

// A dedicated pane, below the default tooltipPane (650), so the pad's
// permanent label always renders behind hover/click tooltips (school,
// locations, etc.) instead of racing them for DOM order within a shared pane.
const OGDP_LABEL_PANE = 'ogdpLabelPane'

export function buildOgdpLayer(geojson: GeoJSON.FeatureCollection, map: L.Map): L.GeoJSON {
  if (!map.getPane(OGDP_LABEL_PANE)) {
    const pane = map.createPane(OGDP_LABEL_PANE)
    pane.style.zIndex = '620'
    pane.style.pointerEvents = 'none'
  }

  return L.geoJSON(geojson, {
    style: feature => ogdpStyleFor((feature?.properties?.name ?? '') as string),
    onEachFeature: (feature, layer) => {
      const { name, label } = (feature.properties ?? {}) as Record<string, string | undefined>
      if (!label) return
      const kind = ogdpKindFor(name ?? '')
      const { color } = ogdpStyleFor(name ?? '')

      if (kind === 'pad') {
        layer.bindTooltip(`<span style="color:${color}">${label}</span>`, {
          permanent: true, direction: 'center', className: 'ogdp-label', pane: OGDP_LABEL_PANE,
        }).openTooltip()
        return
      }

      if (!kind) return
      attachPerimeterLabel(layer as L.Polyline, label, color ?? Colors.fallback, OGDP_LABEL_TARGET_COUNT[kind])
    },
  })
}

export interface LegendItem {
  label: string
  type?: 'circle' | 'line' | 'polygon'
  color?: string
  fillColor?: string
  weight?: number
  rotation?: number
  dashArray?: string
  svg?: string
}

type LegendPosition = 'bottomleft' | 'bottomright' | 'topleft' | 'topright'

export function buildLocationsLayer(geojson: GeoJSON.FeatureCollection): L.GeoJSON {
  return L.geoJSON(geojson, {
    pointToLayer: (feature, latlng) => createLocationMarker(latlng, feature.properties ?? {}),
    onEachFeature: (feature, layer) => {
      layer.bindTooltip(buildLocationTooltip(feature.properties as Record<string, any>), { sticky: true, opacity: 0.9 })
    },
  })
}

function buildLocationTooltip(p: Record<string, any>): string {
  return [
    `<b>${p.location_name}</b>`,
    p.operator ?? '',
    `Wells: ${p.wells} (${p.wells_active ?? 0} active, ${p.wells_pa ?? 0} plugged)`,
    p.wells_lp ? `Low Producing: ${p.wells_lp}` : '',
    p.spills ? `Spills: ${p.spills}` : '',
    p.complaints ? `Complaints: ${p.complaints}` : '',
    p.first_spud ? `Spud: ${formatDate(p.first_spud, "MMM 'YY")}–${formatDate(p.last_spud, "MMM 'YY")}` : '',
    p.first_plugged ? `Plugged: ${formatDate(p.first_plugged, "MMM 'YY")}–${formatDate(p.last_plugged, "MMM 'YY")}` : '',
    p.frac_water_mm_gal ? `Frac Water: ${p.frac_water_mm_gal.toLocaleString()}M gal` : '',
    p.frac_sand_tons ? `Frac Sand: ${p.frac_sand_tons.toLocaleString()} tons` : '',
    p.distance_feet ? `Distance to School: ${Number(p.distance_feet).toLocaleString()} ft` : '',
  ].filter(Boolean).join('<br>')
}

const EARTH_RADIUS_M = 6371000

function destinationPoint(start: L.LatLngExpression, distanceM: number, bearingRad: number): L.LatLng {
  const origin = L.latLng(start)
  const lat1 = origin.lat * Math.PI / 180
  const lon1 = origin.lng * Math.PI / 180
  const angularDistance = distanceM / EARTH_RADIUS_M

  const lat2 = Math.asin(
    Math.sin(lat1) * Math.cos(angularDistance) + Math.cos(lat1) * Math.sin(angularDistance) * Math.cos(bearingRad)
  )
  const lon2 = lon1 + Math.atan2(
    Math.sin(bearingRad) * Math.sin(angularDistance) * Math.cos(lat1),
    Math.cos(angularDistance) - Math.sin(lat1) * Math.sin(lat2)
  )

  return L.latLng(lat2 * 180 / Math.PI, lon2 * 180 / Math.PI)
}

// Approximates a circle as a many-sided polygon ring. L.Circle can't carry a
// leaflet-textpath label (the plugin only patches L.Polyline), so the school
// radius rings are built this way instead, same as the OGDP buffer rings.
function circlePoints(center: L.LatLngExpression, radiusM: number, segments = 72): L.LatLng[] {
  const points: L.LatLng[] = []
  for (let i = 0; i < segments; i++) {
    points.push(destinationPoint(center, radiusM, (i / segments) * 2 * Math.PI))
  }
  return points
}

export function buildSchoolLayer(geojson: GeoJSON.FeatureCollection, map: L.Map): L.GeoJSON {
  const icon = L.icon({
    iconUrl: `${import.meta.env.BASE_URL}/school.svg`,
    iconSize: [32, 32], iconAnchor: [16, 16], popupAnchor: [0, -32], tooltipAnchor: [0, -16],
  })
  const layer = L.geoJSON(geojson, {
    pointToLayer: (_feature, latlng) => L.marker(latlng, { icon }),
    onEachFeature: (feature, layer) => {
      const p = feature.properties as Record<string, any>
      layer.bindTooltip(buildSchoolTooltip(p), { permanent: false, direction: 'top', opacity: 0.9 })
      const oneMileRing = L.polygon(circlePoints(latlng(p), ONE_MILE_M), { color: '#ff7f00', weight: 2, opacity: 1, fillOpacity: 0.03, dashArray: '8,8', interactive: false })
      attachPerimeterLabel(oneMileRing as unknown as L.Polyline, '1 Mile', '#ff7f00', 3)
      oneMileRing.addTo(map)
      const twoThousandRing = L.polygon(circlePoints(latlng(p), TWO_THOUSAND_FT_M), { color: '#cc0000', weight: 2, opacity: 1, fillOpacity: 0.05, dashArray: '6,6', interactive: false })
      attachPerimeterLabel(twoThousandRing as unknown as L.Polyline, '2,000 ft', '#cc0000', 2)
      twoThousandRing.addTo(map)
    },
  })
  return layer
}

export function buildSchoolTooltip(p: Record<string, any>): string {
  const gradeRange = p.low_grade === p.high_grade ? `${p.low_grade}` : `${p.low_grade}–${p.high_grade}`
  const hasWells = !!p.wells
  return [
    `<b>(${p.rank}) ${p.school_name}</b>`,
    gradeRange,
    hasWells ? '<hr><b>One Mile Statistics</b>' : '',
    hasWells ? `Wells: ${p.wells.toLocaleString()} (${p.wells_active.toLocaleString()} active, ${p.wells_pa.toLocaleString()} plugged)` : '',
    p.wells_lp ? `Low Producing (≤1 Mile): ${p.wells_lp.toLocaleString()}` : '',
    p.wells_lp_school_2kft ? `Low Producing (≤2,000 ft): ${p.wells_lp_school_2kft.toLocaleString()}` : '',
    p.spills ? `Spills: ${p.spills.toLocaleString()}` : '',
    p.complaints ? `Complaints: ${p.complaints.toLocaleString()}` : '',
  ].filter(Boolean).join('<br>')
}

export function buildSpillsLayer(geojson: GeoJSON.FeatureCollection): L.GeoJSON {
  return L.geoJSON(geojson, {
    pointToLayer: (feature, latlng) => createSpillMarker(latlng, feature.properties ?? {}),
    onEachFeature: (feature, layer) => {
      const tooltip = buildSpillTooltip(feature.properties as Record<string, any>)
      const group = layer as L.LayerGroup
      const layers = group.getLayers()
      const hitTarget = layers[layers.length - 1]
      hitTarget.bindTooltip(tooltip, { sticky: true, opacity: 0.9 })
    },
  })
}

function buildSpillTooltip(p: Record<string, any>): string {
  return [
    p.spill_type == 'Historical' ? '<b>Historical Release</b>' : '<b>Recent Spill</b>',
    p.operator ?? '',
    p.facility_type ? p.facility_type : '',
    p.api_number ? `API: ${formatIdentifier('apiNumber', p.api_number)}` : '',
    p.date_of_discovery ? `Discovered: ${formatDate(p.date_of_discovery)}` : '',
    p.initial_report_date ? `Reported: ${formatDate(p.initial_report_date)}` : '',
    p.distance_feet ? `Distance to School: ${Math.round(p.distance_feet).toLocaleString()} feet` : '',
  ].filter(Boolean).join('<br>')
}

export function buildTanksLayer(geojson: GeoJSON.FeatureCollection): L.GeoJSON {
  return L.geoJSON(geojson, {
    pointToLayer: (feature, latlng) => createTankMarker(latlng, feature.properties ?? {}),
    onEachFeature: (feature, layer) => {
      const tooltip = buildTankTooltip(feature.properties as Record<string, any>)
      const group = layer as L.LayerGroup
      const layers = group.getLayers()
      const hitTarget = layers[layers.length - 1]
      hitTarget.bindTooltip(tooltip, { sticky: true, opacity: 0.9 })
    },
  })
}

function buildTankTooltip(p: Record<string, any>): string {
  return [
    `<b>${p.name || 'Tank'} (${p.facility_status})</b>`,
    p.operator ?? '',
    p.distance_feet ? `Distance: ${Math.round(p.distance_feet).toLocaleString()} feet` : '',
  ].filter(Boolean).join('<br>')
}

export function buildWellsLayer(geojson: GeoJSON.FeatureCollection): L.GeoJSON {
  return L.geoJSON(geojson, {
    pointToLayer: (feature, latlng) => createWellMarker(latlng, feature.properties ?? {}),
    onEachFeature: (feature, layer) => {
      const tooltip = buildWellTooltip(feature.properties as Record<string, any>)
      const group = layer as L.LayerGroup
      const layers = group.getLayers()
      const hitTarget = layers[layers.length - 1]
      hitTarget.bindTooltip(tooltip, { sticky: true, opacity: 0.9 })
    },
  })
}

function buildWellTooltip(p: Record<string, any>): string {
  return [
    `<b>${p.well_title || p.well_name || 'Well'} (${p.facility_status})</b>`,
    p.operator ?? '',
    p.api_number ? `API: ${formatIdentifier('apiNumber', p.api_number)}` : '',
    p.spud_date ? `Spud: ${formatDate(p.spud_date)}` : '',
    p.facility_status === 'PA' && p.status_date ? `Plugged: ${formatDate(p.status_date)}` : '',
    p.distance_feet ? `Distance to School: ${Math.round(p.distance_feet).toLocaleString()} feet` : '',
    p.lp_designation ? `<span style="color:#f90">${p.lp_designation}</span>` : '',
  ].filter(Boolean).join('<br>')
}

export function createLegend(items: LegendItem[], position: LegendPosition = 'bottomleft'): L.Control {
  const control = L.Control.extend({
    options: { position },
    onAdd() {
      const container = L.DomUtil.create('div', 'leaflet-legend')
      container.style.cssText = 'background:#fff;border-radius:4px;border:1px solid #ccc;padding:3px 5px 3px 8px;font-size:12px;line-height:1.25'
      L.DomEvent.disableClickPropagation(container)

      container.innerHTML = items.map(item => {
        const icon = item.svg
          ? `<img src="${item.svg}" style="width:12px;height:12px;margin-right:8px;vertical-align:middle;display:inline-block">`
          : (() => {
              const isLine = item.type === 'line'
              const dash = item.dashArray
                ? `background:${item.fillColor ?? 'transparent'};outline:${item.weight ?? 1}px ${item.color} dashed;outline-offset:-${item.weight ?? 1}px;`
                : isLine
                  ? `background-color:${item.color};`
                  : `background-color:${item.fillColor ?? item.color};border:${item.weight ?? 1}px solid ${item.color};`
              const shape = item.type === 'circle'
                ? 'border-radius:50%;'
                : isLine
                  ? ''
                  : item.rotation ? `transform:rotate(${item.rotation}deg);` : ''
              const size = isLine ? `width:16px;height:${item.weight ?? 2}px;` : 'width:8px;height:8px;'
              return `<span style="display:inline-block;${size}${dash}${shape}margin-right:8px;vertical-align:middle;"></span>`
            })()
        return `<div style="margin-bottom:6px;display:flex;align-items:center">${icon}<span style="font-size:12px;vertical-align:middle">${item.label}</span></div>`
      }).join('')

      return container
    },
  })
  return new control()
}

export function createLocationMarker(latlng: L.LatLngExpression, props: Record<string, any>): L.CircleMarker {
  const radius = flanneryRadius(props.wells ?? 0)
  const marker = L.circleMarker(latlng, {
    radius,
    color: '#12aa4e',
    fillColor: '#12aa4e',
    fillOpacity: 0.25,
    weight: 2,
    dashArray: '4, 4',
  })

  marker.on('dblclick', (e: L.LeafletMouseEvent) => {
    const facId = props.location_id as string | undefined
    if (facId) {
      window.open(`${ECMC_BASE}/Facility/FacilityDetail?facid=${facId}`, '_blank')
      L.DomEvent.stopPropagation(e)
    }
  })

  return marker
}

export function createSpillMarker(latlng: L.LatLngExpression, props: Record<string, any>): L.LayerGroup {
  const marker = L.marker(latlng, { icon: L.divIcon({
    className: 'custom-diamond-icon',
    html: '<div style="width:8px;height:8px;background-color:rgba(255,105,180,0.2);transform:rotate(45deg);border:1px solid #ff1493;pointer-events:none"></div>',
    iconSize: [8, 8], iconAnchor: [4, 4],
  })})
  const hitTarget = L.circleMarker(latlng, { radius: 12, color: 'transparent', fillColor: 'transparent', fillOpacity: 0, weight: 0, interactive: true })

  const openLink = (e: L.LeafletMouseEvent) => {
    const doc = props.document_number as string | undefined
    if (doc) {
      window.open(`${ECMC_BASE}/Incidents/reportDetail?report=spill&doc_num=${doc}`, '_blank')
      L.DomEvent.stopPropagation(e)
    }
  }

  marker.on('dblclick', openLink)
  hitTarget.on('dblclick', openLink)

  return L.layerGroup([hitTarget, marker])
}

export function createTankMarker(latlng: L.LatLngExpression, props: Record<string, any>): L.LayerGroup {
  const marker = L.marker(latlng, { icon: L.icon({
    iconUrl: `${import.meta.env.BASE_URL}/tank.svg`,
    iconSize: [12, 12],
    iconAnchor: [6, 6],
  })})
  const hitTarget = L.circleMarker(latlng, { radius: 20, color: 'transparent', fillColor: 'transparent', fillOpacity: 0, weight: 0, interactive: true })

  const openLink = (e: L.LeafletMouseEvent) => {
    const facId = props.facility_id as string | undefined
    if (facId) {
      window.open(`${ECMC_BASE}/Facility/FacilityDetail?facid=${facId}`, '_blank')
      L.DomEvent.stopPropagation(e)
    }
  }

  marker.on('dblclick', openLink)
  hitTarget.on('dblclick', openLink)

  return L.layerGroup([hitTarget, marker])
}

export function createWellMarker(latlng: L.LatLngExpression, props: Record<string, any>): L.LayerGroup {
  const isLP = !!props.lp_designation
  const color = isLP ? Colors.well_lp : props.facility_status === 'PA' ? Colors.well_pa : Colors.well_active
  const marker = L.circleMarker(latlng, { radius: 4, color, fillColor: color, fillOpacity: 0.4, weight: 1 })
  const hitTarget = L.circleMarker(latlng, { radius: 12, color: 'transparent', fillColor: 'transparent', fillOpacity: 0, weight: 0, interactive: true })

  const openLink = (e: L.LeafletMouseEvent) => {
    const api = props.api_number as string | undefined
    if (api) {
      window.open(`${ECMC_BASE}/Facility/FacilityDetail?api=${api.startsWith('05') ? api.slice(2) : api}`, '_blank')
      L.DomEvent.stopPropagation(e)
    }
  }

  marker.on('dblclick', openLink)
  hitTarget.on('dblclick', openLink)

  return L.layerGroup([marker, hitTarget])
}

function flanneryRadius(wells: number, minWells = 20, maxWells = 64, minRadius = 12, maxRadius = 40): number {
  const clamped = Math.max(minWells, Math.min(maxWells, wells))
  const t = (clamped - minWells) / (maxWells - minWells)
  return minRadius + (maxRadius - minRadius) * Math.sqrt(t)
}

function formatDate(val: string | null | undefined, format = "MMM D 'YY"): string | null {
  if (!val) return null
  const m = moment(val)
  return m.isValid() ? m.format(format) : null
}

export function formatIdentifier(
  type: 'apiNumber' | 'spillDocNumber' | 'relatedFacilities',
  value: string | null,
): string {
  if (!value) return ''
  let url: string, displayValue: string = value, title: string
  switch (type) {
    case 'apiNumber': {
      const cleaned = value.toString().padStart(10, '0')
      url = `${ECMC_BASE}/Facility/FacilityDetail?api=${cleaned.slice(2)}`
      displayValue = `${cleaned.slice(0, 2)}-${cleaned.slice(2, 5)}-${cleaned.slice(5)}`
      title = 'View facility details at the ECMC.'
      break
    }
    case 'spillDocNumber':
      url = `${ECMC_BASE}/Incidents/reportDetail?report=spill&doc_num=${value}`
      displayValue = '<i class="fa fa-up-right-from-square"></i>'
      title = 'View spill document details at the ECMC.'
      break
    case 'relatedFacilities':
      url = `${ECMC_BASE}/Facility/FacilityDetail?fac=${value}`
      title = 'View related facility details at the ECMC.'
      break
    default:
      throw new Error(`Unsupported identifier type: ${type}`)
  }
  return `<a target="_blank" title="${title}" href="${url}">${displayValue}</a>`
}

function latlng(p: Record<string, any>): L.LatLngExpression {
  return [p.latitude, p.longitude]
}